/**
 * Typed errors cho @vbee-holding/video-renderer (bê từ pptx-to-video errors.ts,
 * đổi tên PptxVideoError → VideoRendererError vì engine giờ nhận nhiều loại input).
 *
 * Mỗi lỗi mang `.code` ổn định + `.status` HTTP để backend map thẳng ra response
 * mà không cần `instanceof` từng subclass:
 *
 *   catch (e) { if (e instanceof VideoRendererError) return res.status(e.status)... }
 *   // hoặc switch theo e.code
 *
 * Shape theo convention CustomError của Vbee (message / cause / meta / name).
 */
export type VideoRendererErrorCode =
  | "INVALID_INPUT"
  | "INPUT_TOO_LARGE"
  | "NOT_A_PRESENTATION"
  | "CORRUPT_PPTX"
  | "EMPTY_DECK"
  | "RENDER_FAILED"
  | "CANCELLED";

export class VideoRendererError extends Error {
  readonly code: VideoRendererErrorCode;
  readonly status: number;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: VideoRendererErrorCode,
    status: number,
    message: string,
    opts?: { cause?: unknown; meta?: Record<string, unknown> },
  ) {
    super(message, opts?.cause !== undefined ? { cause: opts.cause } : undefined);
    this.name = new.target.name; // tên subclass cụ thể (InvalidInputError, …)
    this.code = code;
    this.status = status;
    this.meta = opts?.meta;
  }
}

/** Input descriptor sai/rỗng/không hỗ trợ, hoặc path không đọc được. → 400 */
export class InvalidInputError extends VideoRendererError {
  constructor(message: string, opts?: { cause?: unknown; meta?: Record<string, unknown> }) {
    super("INVALID_INPUT", 400, message, opts);
  }
}

/** Input vượt trần byte đã cấu hình. → 413 */
export class InputTooLargeError extends VideoRendererError {
  constructor(message: string, meta?: Record<string, unknown>) {
    super("INPUT_TOO_LARGE", 413, message, { meta });
  }
}

/** Bytes không phải ZIP/OOXML (thiếu chữ ký `PK`). → 415 */
export class NotAPresentationError extends VideoRendererError {
  constructor(message: string, meta?: Record<string, unknown>) {
    super("NOT_A_PRESENTATION", 415, message, { meta });
  }
}

/** Là ZIP nhưng parser không đọc được thành .pptx. → 422 */
export class CorruptPptxError extends VideoRendererError {
  constructor(message: string, opts?: { cause?: unknown; meta?: Record<string, unknown> }) {
    super("CORRUPT_PPTX", 422, message, opts);
  }
}

/** Parse được nhưng không có slide nào. → 422 */
export class EmptyDeckError extends VideoRendererError {
  constructor(message: string, meta?: Record<string, unknown>) {
    super("EMPTY_DECK", 422, message, { meta });
  }
}

/** Render/encode thất bại (canvas unavailable, ffmpeg lỗi, …). → 500 */
export class RenderError extends VideoRendererError {
  constructor(message: string, opts?: { cause?: unknown; meta?: Record<string, unknown> }) {
    super("RENDER_FAILED", 500, message, opts);
  }
}

/** Caller hủy render qua AbortSignal. → 499 (client closed request) */
export class CancelledError extends VideoRendererError {
  constructor(message = "Render cancelled", meta?: Record<string, unknown>) {
    super("CANCELLED", 499, message, { meta });
  }
}
